import { useCallback, useEffect, useRef, useState } from 'react';

interface Options<T> {
  value: T;
  isSaving?: boolean;
  scopeKey: string;
  onSave: (value: T) => void;
  onDiscard?: () => void;
}

/**
 * Queues one-cell RN confirmation/verification clicks made while another quiet save is
 * running. Updates are keyed by saved row id, applied to the newest value and sent as one
 * follow-up save after the current request completes.
 */
export const useRNQueuedCellSave = <T>({ value, isSaving, scopeKey, onSave, onDiscard }: Options<T>) => {
  const queuedUpdatesRef = useRef(new Map<string, (current: T) => T>());
  const onDiscardRef = useRef(onDiscard);
  const [queuedKeys, setQueuedKeys] = useState<Set<string>>(new Set());
  onDiscardRef.current = onDiscard;

  useEffect(() => {
    if (queuedUpdatesRef.current.size > 0) onDiscardRef.current?.();
    queuedUpdatesRef.current.clear();
    setQueuedKeys(new Set());
  }, [scopeKey]);

  useEffect(() => () => {
    if (queuedUpdatesRef.current.size > 0) onDiscardRef.current?.();
  }, []);

  const saveOrQueue = useCallback((key: string, update: (current: T) => T) => {
    if (!isSaving) {
      onSave(update(value));
      return;
    }
    const previous = queuedUpdatesRef.current.get(key);
    queuedUpdatesRef.current.set(key, previous
      ? (current) => update(previous(current))
      : update);
    setQueuedKeys((current) => new Set(current).add(key));
  }, [isSaving, onSave, value]);

  useEffect(() => {
    if (isSaving || queuedUpdatesRef.current.size === 0) return;
    const updates = Array.from(queuedUpdatesRef.current.values());
    queuedUpdatesRef.current.clear();
    setQueuedKeys(new Set());
    onSave(updates.reduce((current, update) => update(current), value));
  }, [isSaving, onSave, value]);

  return { saveOrQueue, isQueued: (key: string) => queuedKeys.has(key) };
};

export default useRNQueuedCellSave;
